import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScaleIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useApi, useFormSubmit, useAsyncOperation } from '../hooks/useApi';
import { weighbridgesAPI, portsAPI, molesAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal, { ConfirmModal } from '../components/Modal';
import Button from '../components/Button';
import Input from '../components/Input';
import Table from '../components/Table';

const WeighbridgesPage = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedWeighbridge, setSelectedWeighbridge] = useState(null);

  // Fetch weighbridges data from API
  const { data: weighbridgesData, loading, refetch } = useApi(weighbridgesAPI.getAll);
  const weighbridges = weighbridgesData?.weighbridges || [];

  // Fetch supporting data for forms
  const { data: portsData, loading: portsLoading } = useApi(portsAPI.getAll);
  const ports = portsData?.ports || [];

  const { data: molesData, loading: molesLoading } = useApi(molesAPI.getAll);
  const moles = molesData?.moles || [];

  const { data: usersData, loading: usersLoading } = useApi(() => usersAPI.getByRole('MANAGER'));
  const managers = usersData?.users || [];

  // Form submission hooks
  const { submit: submitCreate, loading: createLoading } = useFormSubmit(
    weighbridgesAPI.create,
    {
      onSuccess: () => {
        setIsAddModalOpen(false);
        refetch();
      },
      successMessage: 'Weighbridge created successfully',
    }
  );

  const { submit: submitUpdate, loading: updateLoading } = useFormSubmit(
    (data) => weighbridgesAPI.update(selectedWeighbridge.id, data),
    {
      onSuccess: () => {
        setIsEditModalOpen(false);
        setSelectedWeighbridge(null);
        refetch();
      },
      successMessage: 'Weighbridge updated successfully',
    }
  );

  const { execute: executeDelete, loading: deleteLoading } = useAsyncOperation();

  // Event handlers
  const handleEdit = (weighbridge) => {
    setSelectedWeighbridge(weighbridge);
    setIsEditModalOpen(true);
  };

  const handleDelete = (weighbridge) => {
    setSelectedWeighbridge(weighbridge);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedWeighbridge?.id) return;
    try {
      await executeDelete(
        () => weighbridgesAPI.delete(selectedWeighbridge.id),
        { showSuccessToast: true, successMessage: t('weighbridges.deleteSuccess', 'Weighbridge deleted successfully') }
      );
      setIsDeleteModalOpen(false);
      setSelectedWeighbridge(null);
      refetch();
    } catch {
      /* errors handled in executeDelete */
    }
  };

  const filteredWeighbridges = weighbridges.filter(wb =>
    wb.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wb.port?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wb.mole?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wb.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return t('weighbridges.status.active');
      case 'maintenance': return t('weighbridges.status.maintenance');
      case 'inactive': return t('weighbridges.status.inactive');
      default: return status;
    }
  };

  if (loading) return <LoadingSpinner />;

  // Define columns for the table
  const columns = [
    {
      key: 'name',
      title: t('weighbridges.weighbridgeName'),
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">{value}</div>
      ),
    },
    {
      key: 'location',
      title: t('weighbridges.location'),
      render: (value, row) => (
        <div className="text-sm text-gray-500">
          {row.mole?.name || row.location || 'N/A'}
        </div>
      ),
    },
    {
      key: 'maxCapacity',
      title: t('weighbridges.maxCapacity'),
      render: (value) => (
        <div className="text-sm text-gray-900">
          {(value / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} Ton
        </div>
      ),
    },
    {
      key: 'managers',
      title: t('weighbridges.manager'),
      render: (value, row) => (
        <div className="text-sm text-gray-900">
          {row.managers && row.managers.length > 0 ? (
            row.managers.map((manager, index) => (
              <div key={manager.user.id} className={index > 0 ? 'mt-1' : ''}>
                {manager.user.firstName} {manager.user.lastName}
              </div>
            ))
          ) : (
            'N/A'
          )}
        </div>
      ),
    },
    {
      key: 'status',
      title: t('tables.status'),
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(value)}`}>
          {getStatusText(value)}
        </span>
      ),
    },
    {
      key: 'actions',
      title: t('tables.actions'),
      sticky: true,
      width: '150px',
      render: (value, row) => (
        <div className="flex items-center flex-wrap gap-2">
          {isAdmin() && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleEdit(row)}
                title={t('common.edit')}
              >
                <PencilIcon className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDelete(row)}
                disabled={row._count?.loads > 0}
                title={row._count?.loads > 0 ? t('weighbridges.tooltips.cannotDeleteWithLoads') : t('common.delete')}
                className={row._count?.loads > 0 ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-700"}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ScaleIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('weighbridges.title')}</h1>
            <p className="text-gray-600">{t('weighbridges.subtitle')}</p>
          </div>
        </div>
        {isAdmin() && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>{t('weighbridges.addWeighbridge')}</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="card">
        <div className="flex items-center">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('weighbridges.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>
        </div>
      </div>

      {/* Weighbridges Table */}
      <div className="card">
        <Table
          columns={columns}
          data={filteredWeighbridges}
          loading={loading}
          emptyMessage={t('weighbridges.noWeighbridgesFound')}
        />
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-blue-500 rounded-md flex items-center justify-center">
                <ScaleIcon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="ml-5">
              <div className="text-sm font-medium text-gray-500">{t('weighbridges.stats.total')}</div>
              <div className="text-2xl font-bold text-gray-900">{weighbridges.length}</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-green-500 rounded-md flex items-center justify-center">
                <ScaleIcon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="ml-5">
              <div className="text-sm font-medium text-gray-500">{t('weighbridges.stats.active')}</div>
              <div className="text-2xl font-bold text-gray-900">
                {weighbridges.filter(wb => wb.status === 'active').length}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-yellow-500 rounded-md flex items-center justify-center">
                <ScaleIcon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="ml-5">
              <div className="text-sm font-medium text-gray-500">{t('weighbridges.stats.maintenance')}</div>
              <div className="text-2xl font-bold text-gray-900">
                {weighbridges.filter(wb => wb.status === 'maintenance').length}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-purple-500 rounded-md flex items-center justify-center">
                <ScaleIcon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="ml-5">
              <div className="text-sm font-medium text-gray-500">{t('weighbridges.stats.totalCapacity')}</div>
              <div className="text-2xl font-bold text-gray-900">
                {(weighbridges.reduce((sum, wb) => sum + wb.maxCapacity, 0) / 1000).toFixed(0)}t
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      <WeighbridgeForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={submitCreate}
        loading={createLoading}
        ports={ports}
        portsLoading={portsLoading}
        moles={moles}
        molesLoading={molesLoading}
        managers={managers}
        managersLoading={usersLoading}
        title={t('weighbridges.form.newWeighbridge')}
        submitText={t('common.create')}
      />

      {/* Edit Modal */}
      <WeighbridgeForm
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedWeighbridge(null);
        }}
        onSubmit={submitUpdate}
        loading={updateLoading}
        ports={ports}
        portsLoading={portsLoading}
        moles={moles}
        molesLoading={molesLoading}
        managers={managers}
        managersLoading={usersLoading}
        title={t('weighbridges.form.editWeighbridge')}
        submitText={t('common.update')}
        initialData={selectedWeighbridge}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedWeighbridge(null);
        }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title={t('weighbridges.messages.confirmDelete')}
        message={`${t('weighbridges.messages.confirmDelete')} "${selectedWeighbridge?.name}"? ${t('weighbridges.messages.deleteWarning')}`}
        confirmText={t('common.delete')}
        variant="danger"
      />
    </div>
  );
};

// Weighbridge Form Component
const WeighbridgeForm = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  ports,
  portsLoading,
  moles,
  molesLoading,
  managers,
  managersLoading,
  title,
  submitText,
  initialData = {},
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    portId: '',
    moleId: '',
    location: '',
    maxCapacity: '',
    managerIds: [],
    status: 'active'
  });

  const [selectedPortMoles, setSelectedPortMoles] = useState([]);

  React.useEffect(() => {
    if (isOpen) {
      const existingManagerIds = initialData?.managers?.map(m => m.user.id) || 
                                 (initialData?.managerId ? [initialData.managerId] : []);
      setFormData({
        name: initialData?.name || '',
        portId: initialData?.portId || '',
        moleId: initialData?.moleId || '',
        location: initialData?.location || '',
        maxCapacity: initialData?.maxCapacity ? (initialData.maxCapacity / 1000) : '',
        managerIds: existingManagerIds,
        status: initialData?.status || 'active'
      });
    } else {
      // Reset form when modal closes
      setFormData({
        name: '',
        portId: '',
        moleId: '',
        location: '',
        maxCapacity: '',
        managerIds: [],
        status: 'active'
      });
    }
  }, [isOpen, initialData?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- initialize weighbridge form when opening or switching the edited weighbridge

  // Filter moles based on selected port
  React.useEffect(() => {
    if (formData.portId && moles.length > 0) {
      const portMoles = moles.filter(mole => mole.portId === formData.portId);
      setSelectedPortMoles(portMoles);
      
      // Clear mole selection if the current mole doesn't belong to the selected port
      if (formData.moleId && !portMoles.find(m => m.id === formData.moleId)) {
        setFormData(prev => ({ ...prev, moleId: '' }));
      }
    } else {
      setSelectedPortMoles([]);
    }
  }, [formData.portId, moles]); // eslint-disable-line react-hooks/exhaustive-deps -- keep selected mole aligned with the currently selected port

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic form validation
    if (!formData.name || !formData.portId || !formData.location || !formData.maxCapacity) {
      alert(t('weighbridges.validations.nameRequired'));
      return;
    }
    
    if (formData.maxCapacity <= 0) {
      alert(t('weighbridges.validations.validCapacity'));
      return;
    }
    
    const submitData = {
      ...formData,
      maxCapacity: parseFloat(formData.maxCapacity) * 1000,
      moleId: formData.moleId || null,
      // Ensure managerIds is always an array
      managerIds: formData.managerIds || []
    };
    
    onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleManagerChange = (managerId) => {
    setFormData(prev => {
      const currentIds = prev.managerIds || [];
      const isSelected = currentIds.includes(managerId);
      
      if (isSelected) {
        // Remove manager
        return {
          ...prev,
          managerIds: currentIds.filter(id => id !== managerId)
        };
      } else {
        // Add manager
        return {
          ...prev,
          managerIds: [...currentIds, managerId]
        };
      }
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={loading} form="weighbridge-form">
            {submitText}
          </Button>
        </>
      }
    >
      <form id="weighbridge-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div className="md:col-span-2">
            <Input
              label={t('weighbridges.form.name')}
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('weighbridges.form.namePlaceholder')}
              required
            />
          </div>

          {/* Port */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('weighbridges.form.selectPort')} *
            </label>
            <select
              name="portId"
              value={formData.portId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
              disabled={portsLoading}
            >
              <option value="">{t('weighbridges.form.portPlaceholder')}</option>
              {ports.map((port) => (
                <option key={port.id} value={port.id}>
                  {port.name}
                </option>
              ))}
            </select>
          </div>

          {/* Mole */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('weighbridges.form.selectMole')}
            </label>
            <select
              name="moleId"
              value={formData.moleId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={!formData.portId || molesLoading}
            >
              <option value="">{t('weighbridges.form.molePlaceholder')}</option>
              {selectedPortMoles.map((mole) => (
                <option key={mole.id} value={mole.id}>
                  {mole.name}
                </option>
              ))}
            </select>
          </div>

          {/* Location */}
          <div>
            <Input
              label={t('weighbridges.form.location')}
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder={t('weighbridges.form.locationPlaceholder')}
              required
            />
          </div>

          {/* Max Capacity */}
          <div>
            <Input
              label={t('weighbridges.form.maxCapacity')}
              name="maxCapacity"
              type="number"
              value={formData.maxCapacity}
              onChange={handleChange}
              placeholder={t('weighbridges.form.capacityPlaceholder')}
              min="0"
              step="0.01"
              required
            />
          </div>

          {/* Managers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('weighbridges.form.selectManagers', 'Select Managers')}
            </label>
            {managersLoading ? (
              <div className="text-gray-500 text-sm">{t('common.loading', 'Loading...')}</div>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                {managers.length === 0 ? (
                  <div className="text-gray-500 text-sm">{t('weighbridges.form.noManagersAvailable', 'No managers available')}</div>
                ) : (
                  managers.map((manager) => (
                    <label key={manager.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.managerIds?.includes(manager.id) || false}
                        onChange={() => handleManagerChange(manager.id)}
                        className="checkbox-app"
                      />
                      <span className="text-sm text-gray-700">
                        {manager.firstName} {manager.lastName} <span className="text-gray-500">(@{manager.username})</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            )}
            {formData.managerIds?.length > 0 && (
              <div className="mt-2 text-sm text-gray-600">
                {t('weighbridges.form.selectedManagers', 'Selected managers')}: {formData.managerIds.length}
              </div>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('weighbridges.form.status')} *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="active">{t('weighbridges.status.active')}</option>
              <option value="maintenance">{t('weighbridges.status.maintenance')}</option>
              <option value="inactive">{t('weighbridges.status.inactive')}</option>
            </select>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default WeighbridgesPage;